import cn from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';

import ModalEventsHandler from 'shared/components/windows/ModalEventsHandler';
import {
  PopupEventType,
  PopupPosition,
  PopupProps,
  PopupRef
} from 'shared/entities/components/Popup';
import { useUiStore } from 'stores/index';

import BasePopup from '../BasePopup';
import { usePopupTriggerAttachment } from '../usePopupTriggerAttachment';
import {
  POPUP_MAX_HEIGHT_RATIO,
  usePopupViewportHeight
} from '../usePopupViewportHeight';

type Props = PopupProps;

const DEFAULT_MAX_WIDTH = 320;
const DEFAULT_GAP = 4;
const DEFAULT_MAX_HEIGHT = `${POPUP_MAX_HEIGHT_RATIO * 100}vh`;

const PopupContainer = React.forwardRef<PopupRef, Props>(
  (
    {
      trigger,
      children,
      position = [PopupPosition.topCenter],
      on,
      className,
      triggerClassname,
      containerFullHeight = false,
      contentStyle,
      closeOnDocumentClick = true,
      attachToTriggerOnScroll = false,
      repositionDeps,
      onClose,
      onOpen,
      disabled = false,
      closeOnEscape = true,
      isOpen,
      nested,
      maxWidth,
      offsetX = 0,
      offsetY = 0,
      gap = DEFAULT_GAP,
      keepTooltipInside = true,
      fitViewportHeight = false
    }: Props,
    ref
  ) => {
    const uiStore = useUiStore();
    const [zIndex, setZIndex] = React.useState<number>(1000);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const popupRef = React.useRef<{
      open: () => void;
      close: () => void;
      toggle: () => void;
      setPosition: () => void;
    } | null>(null);
    const [opened, setOpened] = React.useState(false);

    const isPopupOpened = isOpen ?? opened;

    const handleOpen = React.useCallback(() => {
      setOpened(true);
      onOpen?.();
    }, [onOpen]);

    const handleClose = React.useCallback(() => {
      setOpened(false);
      onClose?.();
    }, [onClose]);

    const reposition = React.useCallback(() => {
      popupRef.current?.setPosition();
    }, []);

    const closePopup = React.useCallback(() => {
      popupRef.current?.close();
    }, []);

    const { attachmentStyle } = usePopupTriggerAttachment({
      attachToTrigger: attachToTriggerOnScroll,
      opened: isPopupOpened,
      reposition,
      close: closePopup,
      repositionDeps,
      isMobile: uiStore.isMobile,
      triggerRef: containerRef
    });

    const viewportMaxHeight = usePopupViewportHeight({
      active: fitViewportHeight && isPopupOpened,
      triggerRef: containerRef,
      position,
      gap,
      reposition
    });

    React.useImperativeHandle(ref, () => {
      return {
        setPosition: reposition,
        container: containerRef.current
      };
    });

    const actualCloseOnEscape =
      on === PopupEventType.hover ? false : closeOnEscape;

    const actualMaxWidth = maxWidth ?? DEFAULT_MAX_WIDTH;
    const actualMobileMaxWidth = maxWidth ?? '70%';

    const isHoverable = Array.isArray(on)
      ? on.includes(PopupEventType.hover)
      : on === PopupEventType.hover;

    return (
      <div
        ref={containerRef}
        className={cn(
          containerFullHeight && 'popup__container_full-height',
          triggerClassname
        )}
      >
        <BasePopup
          ref={popupRef}
          on={on}
          open={isOpen}
          trigger={trigger}
          position={position}
          className={cn('popup', isHoverable && 'popup-hoverable', className)}
          closeOnDocumentClick={closeOnDocumentClick}
          closeOnEscape={false}
          onClose={handleClose}
          onOpen={handleOpen}
          contentStyle={{
            background: 'none',
            // отступ от триггера задаётся через gap: паддинг обёртки утапливал бы
            // контент внутрь измеряемого прямоугольника и ломал выравнивание
            // по поперечной оси
            padding: 0,
            border: 'none',
            boxShadow: 'none',
            maxWidth: uiStore.isMobileSize
              ? actualMobileMaxWidth
              : actualMaxWidth,
            width: 'auto',
            maxHeight: viewportMaxHeight ?? DEFAULT_MAX_HEIGHT,
            zIndex,
            // размер зоны-мостика над зазором для .popup-hoverable-content
            ['--popup-gap' as string]: `${gap}px`,
            ...contentStyle,
            ...attachmentStyle
          }}
          arrow={false}
          disabled={disabled}
          nested={nested}
          offsetX={offsetX}
          offsetY={offsetY}
          gap={gap}
          keepTooltipInside={keepTooltipInside}
        >
          {/* @ts-ignore TODO bug in i18next https://github.com/i18next/react-i18next/issues/1543 */}
          {(close, opened) => {
            return (
              <ModalEventsHandler
                onClose={close}
                opened={opened}
                onZIndexChange={setZIndex}
                closeOnEscape={actualCloseOnEscape}
                render={({ onClose }) => {
                  return React.isValidElement(children) || children === null ? (
                    <>{children}</>
                  ) : (
                    children(onClose, opened)
                  );
                }}
              />
            );
          }}
        </BasePopup>
      </div>
    );
  }
);

export default observer(PopupContainer);
